import { apiFetch } from './client'

/**
 * s65 / PE -- perfil da empresa (`GET`/`PUT /api/tenants/{id}/company-profile`, modules/tenant_management/api/router.py).
 * Le quem tem `tenant.read`; GRAVA so o consultor da Gente (`tenant.company_profile.set`, so platform_admin -- E2 A).
 * O PUT SUBSTITUI o perfil inteiro: a tela manda sempre os 12 campos. Listas fechadas iguais as do servidor.
 *
 * s67 / PE2 (Marcelo 25/10/2026 11:55, "APROVADO PE2 ; F1-F4 sim"): + `locais` (F3, no lugar do texto `unidades`, que
 * continua guardado -- G2), `fontes` (F1, de onde veio cada campo) e a CONFIRMACAO do cliente (F4: quem e quando;
 * `POST .../company-profile/confirm`, so administrador e RH do cliente -- G1/G4). O PUT manda agora 14 campos.
 */
export const COMPANY_PROFILE_WRITE_PERMISSION = 'tenant.company_profile.set'
export const COMPANY_PROFILE_CONFIRM_PERMISSION = 'tenant.company_profile.confirm'

export interface Local {
  nome: string
  cidade: string | null
  uf: string | null
  tipo: string
}

export interface CompanyProfile {
  setor: string | null
  ramo: string | null
  uf: string | null
  cidade: string | null
  porte: string | null
  atividades: string | null
  produtos: string[]
  processos: string[]
  unidades: string | null
  turnos: string[]
  certificacoes: string[]
  mercado: string[]
  // s67 / PE2
  locais: Local[]
  fontes: Record<string, string>
}

/** s67 / PE2 (F4): a confirmacao do cliente -- so leitura (vem do servidor; o consultor muda o perfil -> zera). */
export interface ProfileStatus {
  confirmedAt: string | null
  confirmedBy: string | null
}
export type ProfileWithStatus = CompanyProfile & ProfileStatus

/** O contador da tela: 12 campos -- "unidades" conta como o campo dos LOCAIS (o texto antigo ou a lista nova). */
export const PROFILE_FIELDS: readonly (keyof CompanyProfile)[] = [
  'setor', 'ramo', 'uf', 'cidade', 'porte', 'atividades', 'produtos', 'processos', 'unidades', 'turnos', 'certificacoes',
  'mercado',
]

export const SETORES: [string, string][] = [
  ['industria', 'Indústria'], ['servicos', 'Serviços'], ['comercio', 'Comércio'], ['agro', 'Agronegócio'],
  ['publico', 'Setor público'], ['terceiro_setor', 'Terceiro setor'],
]
export const PORTES: [string, string][] = [
  ['ate_19', 'até 19 empregados'], ['20_99', 'de 20 a 99'], ['100_499', 'de 100 a 499'], ['500_999', 'de 500 a 999'],
  ['1000_mais', '1.000 ou mais'],
]
export const UFS = 'AC AL AP AM BA CE DF ES GO MA MT MS MG PA PB PR PE PI RJ RN RS RO RR SC SP SE TO'.split(' ')
export const TURNOS: [string, string][] = [
  ['administrativo', 'administrativo'], ['dois_turnos', '2 turnos'], ['tres_turnos', '3 turnos'],
  ['escala_12x36', 'escala 12x36'], ['escala_6x1', 'escala 6x1'], ['noturno', 'noturno'],
  ['revezamento', 'turnos de revezamento'],
]
export const MERCADOS: [string, string][] = [
  ['pessoa_fisica', 'pessoa física'], ['empresas', 'empresas'], ['governo', 'governo'], ['exportacao', 'exportação'],
]
// s67 / PE2 (F3): tipos de local -- iguais aos de TIPOS_LOCAL no servidor
export const TIPOS_LOCAL: [string, string][] = [
  ['fabrica', 'fábrica'], ['escritorio', 'escritório'], ['loja', 'loja'], ['centro_distribuicao', 'centro de distribuição'],
  ['campo_obra', 'campo / obra'], ['remoto', 'remoto'],
]
export const MAX_LOCAIS = 30

const VAZIO: CompanyProfile = {
  setor: null, ramo: null, uf: null, cidade: null, porte: null, atividades: null, produtos: [], processos: [],
  unidades: null, turnos: [], certificacoes: [], mercado: [], locais: [], fontes: {},
}

type Resposta = Partial<CompanyProfile> & { confirmed_at?: string | null; confirmed_by?: string | null }

function normaliza(r: Resposta): ProfileWithStatus {
  const lista = (v: unknown) => (Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : [])
  const locais = Array.isArray(r.locais)
    ? r.locais
        .filter((x): x is Local => !!x && typeof x === 'object' && typeof (x as Local).nome === 'string')
        .map((x) => ({ nome: x.nome, cidade: x.cidade ?? null, uf: x.uf ?? null, tipo: x.tipo }))
    : []
  const fontes: Record<string, string> = {}
  if (r.fontes && typeof r.fontes === 'object')
    for (const [k, v] of Object.entries(r.fontes)) if (typeof v === 'string') fontes[k] = v
  return {
    ...VAZIO,
    setor: r.setor ?? null, ramo: r.ramo ?? null, uf: r.uf ?? null, cidade: r.cidade ?? null, porte: r.porte ?? null,
    atividades: r.atividades ?? null, unidades: r.unidades ?? null,
    produtos: lista(r.produtos), processos: lista(r.processos), turnos: lista(r.turnos),
    certificacoes: lista(r.certificacoes), mercado: lista(r.mercado),
    locais, fontes, confirmedAt: r.confirmed_at ?? null, confirmedBy: r.confirmed_by ?? null,
  }
}

export const companyProfileKey = (tenantId: string) => ['company-profile', tenantId] as const

export async function fetchCompanyProfile(tenantId: string): Promise<ProfileWithStatus> {
  return normaliza(await apiFetch<Resposta>(`/api/tenants/${encodeURIComponent(tenantId)}/company-profile`))
}

export async function saveCompanyProfile(tenantId: string, p: CompanyProfile): Promise<ProfileWithStatus> {
  const corpo: Record<string, unknown> = {}
  for (const k of PROFILE_FIELDS) {
    const v = p[k]
    corpo[k] = typeof v === 'string' ? (v.trim() ? v.trim() : null) : v
  }
  // s67 / PE2: os locais e as fontes vao junto (o PUT substitui tudo)
  corpo.locais = p.locais
  corpo.fontes = p.fontes
  return normaliza(await apiFetch<Resposta>(`/api/tenants/${encodeURIComponent(tenantId)}/company-profile`, {
    method: 'PUT', body: corpo,
  }))
}

/** s67 / PE2 (F4): o cliente confirma o perfil -- o servidor grava quem e quando. */
export async function confirmCompanyProfile(tenantId: string): Promise<ProfileWithStatus> {
  return normaliza(await apiFetch<Resposta>(`/api/tenants/${encodeURIComponent(tenantId)}/company-profile/confirm`, {
    method: 'POST',
  }))
}

export function preenchidos(p: CompanyProfile): number {
  return PROFILE_FIELDS.filter((k) => {
    if (k === 'unidades' && p.locais.length > 0) return true
    const v = p[k]
    return Array.isArray(v) ? v.length > 0 : typeof v === 'string' && v.trim() !== ''
  }).length
}
